import React, { useState, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import { Box } from '@mui/material';

import BackButton from '@ts-core/components/BackButton';
import { YearMonthSelector, YearMonthDate } from '@ts-core/modules/billing/components/YearMonthSelector';
import { ActivityType, ActivityTypesValues } from '@ts-core/modules/billing/types';
import { StudentActivityAnswersByMonth } from '../components/StudentActivityAnswersByMonth';
import { StudentAssignmentsByMonth } from '../components/StudentAssignmentsByMonth';
import { StudentMediaSessionsByMonth } from '../components/StudentMediaSessionsByMonth';
import { StudentPracticeSessionsByMonth } from '../components/StudentPracticeSessionsByMonth';

import useStudent from 'modules/students/hooks/useStudent';
import useCourse from 'modules/courses/hooks/useCourse';
import { useNewBillingStartingDateComponents } from 'modules/billing/hooks/useNewBillingStartingDateComponents';

export default function StudentActivityByMonthPage() {
  const {
    courseId,
    studentId,
    activityType,
    year: urlYear,
    month: urlMonth,
  } = useParams<{
    courseId: string;
    studentId: string;
    activityType: ActivityType;
    year: string;
    month: string;
  }>();
  const history = useHistory();
  const { year: minYear, month: minMonth } = useNewBillingStartingDateComponents();
  const { student } = useStudent({ courseId, studentId });
  const { course } = useCourse({ courseId });

  const [date, setDate] = useState<YearMonthDate>({ year: urlYear, month: urlMonth });
  const { year, month } = date;

  const handleGoBack = useCallback(() => {
    history.push(`/billing/${year}/${month}/${courseId}`);
  }, [history, courseId, year, month]);

  if (!ActivityTypesValues.includes(activityType)) {
    history.replace(`/billing/${year}/${month}/${courseId}`);
    return null;
  }

  function renderContent() {
    switch (activityType) {
      case 'activities':
        return <StudentActivityAnswersByMonth courseId={courseId} studentId={studentId} year={year} month={month} />;
      case 'assignments':
        return <StudentAssignmentsByMonth courseId={courseId} studentId={studentId} year={year} month={month} />;
      case 'media':
        return <StudentMediaSessionsByMonth courseId={courseId} studentId={studentId} year={year} month={month} />;
      case 'practice':
        return <StudentPracticeSessionsByMonth courseId={courseId} studentId={studentId} year={year} month={month} />;
    }
  }

  return (
    <Box>
      <BackButton noMargin onGoBack={handleGoBack} title={student?.name} subtitle={course?.name} />
      <YearMonthSelector sx={{ mt: 3, mb: 2 }} onChangeDate={setDate} date={date} minYear={minYear} minMonth={minMonth} />
      {renderContent()}
    </Box>
  );
}
